<template>
    <main id="js-page-content" class="forms-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('forms.mine') }}</div>
        </portal>

        <header class="forms-hero">
            <h1 class="forms-hero-title">{{ $t('forms.mine') }}</h1>
            <p class="forms-hero-sub">{{ totalLabel }}</p>
        </header>

        <section v-if="!loading" class="forms-card">
            <div class="ff-toolbar">
                <router-link :to="`/forms/builder`" class="ff-create-btn">
                    <i class="fal fa-plus"></i>
                    <span>{{ $t('forms.new') }}</span>
                </router-link>
                <div class="ff-filter">
                    <i class="fal fa-filter ff-filter-icon"></i>
                    <select v-model="selectedTypeId" class="ff-filter-select">
                        <option :value="null">{{ $t('form_types.filter_all') }}</option>
                        <option
                            v-for="type in types"
                            :key="type.id"
                            :value="type.id"
                        >
                            {{ type.name }}
                        </option>
                    </select>
                </div>
            </div>
            <TabForms :forms="filteredForms" :types="types" />
        </section>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
// import Panel from '@/components/UI/Panel'
import TabForms from '@/components/Model/Form/TabForms'

import { mapState, mapActions } from 'vuex'

export default {
    name: 'Forms',
    components: {
        // Panel,
        TabForms
    },
    created() {
        this.loadAsyncData()
    },
    data: function () {
        return {
            loading: true,
            selectedTypeId: null
        }
    },
    computed: {
        ...mapState('form', {
            forms: (state) => state.forms,
            types: (state) => state.types,
            config: (state) => state.formsConfig
        }),
        filteredForms() {
            if (!this.selectedTypeId) return this.forms
            return this.forms.filter((f) => f.form_type_id === this.selectedTypeId)
        },
        totalLabel() {
            const n = this.filteredForms ? this.filteredForms.length : 0
            const formatted = new Intl.NumberFormat(this.$i18n.locale).format(n)
            const key = n === 1 ? 'forms.list_count_one' : 'forms.list_count_other'
            return this.$t(key, { count: formatted })
        }
    },
    methods: {
        ...mapActions('form', ['getForms']),
        loadAsyncData() {
            this.loading = true
            this.getForms().then(() => {
                this.types.map((type) => {
                    type.forms = this.forms.filter(
                        (form) => form.form_type_id === type.id
                    )
                })
                this.loading = false
            })
        },
        onSort(field, order) {
            this.config.sort = field
            this.config.sortDirection = order

            this.loadAsyncData()
        },
        onFilterChange(filter) {
            var filters = {}
            Object.keys(filter).forEach((element) => {
                filters[element] = filter[element]
            })
            this.config.filters = filters

            this.loadAsyncData()
        },
        onPageChange(page) {
            this.config.page = page

            this.loadAsyncData()
        }
    }
}
</script>

<style scoped>
.forms-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.forms-hero {
    margin-bottom: var(--v2-space-5);
}

.forms-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.forms-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.forms-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.ff-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    padding: var(--v2-space-4) var(--v2-space-5);
    border-bottom: 1px solid var(--v2-border-subtle);
}

.ff-create-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--v2-primary);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff) !important;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    text-decoration: none !important;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.ff-create-btn:hover {
    background: var(--v2-primary-hover);
    border-color: var(--v2-primary-hover);
    color: var(--v2-primary-contrast, #fff) !important;
    text-decoration: none !important;
}

.ff-create-btn i {
    color: inherit !important;
    font-size: 0.8rem;
}

.ff-filter {
    position: relative;
}

.ff-filter-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-text-4);
    font-size: 0.78rem;
    pointer-events: none;
}

.ff-filter-select {
    height: 32px;
    padding: 0 12px 0 28px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    font-family: inherit;
    font-size: 0.82rem;
    color: var(--v2-text-2);
    background: var(--v2-surface-0);
    outline: none;
    cursor: pointer;
    min-width: 200px;
    box-sizing: border-box;
    transition: border-color var(--v2-transition-fast), background var(--v2-transition-fast);
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

.ff-filter-select:hover {
    background: var(--v2-surface-1);
    border-color: var(--v2-border-strong, var(--v2-border));
}

.ff-filter-select:focus {
    border-color: var(--v2-primary) !important;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12) !important;
    outline: none !important;
}

@media (max-width: 900px) {
    .forms-v2 {
        padding: 16px 18px 32px;
    }

    .forms-hero-title {
        font-size: 1.375rem;
    }

    .ff-toolbar {
        flex-direction: column;
        align-items: stretch;
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .ff-create-btn {
        justify-content: center;
    }

    .ff-filter-select {
        width: 100%;
        min-width: 0;
    }
}
</style>
